import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, TextField, Grid, Chip } from '@mui/material';
import { Security } from '@mui/icons-material';
import ImportExportBar from '../../components/ImportExport/ImportExportBar';
import { AuditEvent, getAuditEvents, replaceAuditEvents } from '../../utils/audit';

const Audit: React.FC = () => {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [q, setQ] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => { setEvents(getAuditEvents()); }, []);

  const filtered = useMemo(() => {
    return events.filter(e =>
      (!q || JSON.stringify(e).toLowerCase().includes(q.toLowerCase())) &&
      (!moduleFilter || e.module === moduleFilter) &&
      (!actionFilter || e.action === actionFilter)
    );
  }, [events, q, moduleFilter, actionFilter]);

  const parseCSV = (text: string): Record<string, string>[] => {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
      const cols = line.split(',');
      const row: Record<string, string> = {};
      headers.forEach((h, i) => { row[h] = (cols[i] || '').trim(); });
      return row;
    });
  };

  const onImportCSV = (csvText: string) => {
    // id,timestamp,module,action,details_json
    const rows = parseCSV(csvText);
    const list: AuditEvent[] = rows.map(r => ({
      id: r.id || `${Date.now()}`,
      timestamp: r.timestamp || new Date().toISOString(),
      module: r.module || 'unknown',
      action: r.action || 'unknown',
      details: r.details_json ? JSON.parse(r.details_json) : {},
    }));
    replaceAuditEvents(list);
    setEvents(list);
  };

  const onImportJSON = (jsonText: string) => {
    try {
      const data = JSON.parse(jsonText);
      if (Array.isArray(data)) { replaceAuditEvents(data as AuditEvent[]); setEvents(data as AuditEvent[]); }
    } catch {}
  };

  const getExportCSV = () => {
    const header = 'id,timestamp,module,action,details_json';
    const lines = [header, ...events.map(e => [e.id, e.timestamp, e.module, e.action, JSON.stringify(e.details || {}).replace(/,/g,';')].join(','))];
    return { filename: 'audit.csv', content: lines.join('\n') };
  };
  const getExportJSON = () => ({ filename: 'audit.json', content: JSON.stringify(events, null, 2) });

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Piste d'Audit
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Traçabilité complète des opérations et conformité
        </Typography>
      </Box>

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Security color="primary" />
            <Typography variant="h6">Événements</Typography>
          </Box>
          <ImportExportBar onImportCSV={onImportCSV} onImportJSON={onImportJSON} getExportCSV={getExportCSV} getExportJSON={getExportJSON} />
        </CardContent>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Recherche" value={q} onChange={e => setQ(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Module (ex: ecritures)" value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField fullWidth label="Action (ex: import)" value={actionFilter} onChange={e => setActionFilter(e.target.value)} />
        </Grid>
      </Grid>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date/Heure</TableCell>
              <TableCell>Module</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Détails</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center">Aucun événement</TableCell></TableRow>
            ) : filtered.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell>{new Date(e.timestamp).toLocaleString()}</TableCell>
                <TableCell><Chip size="small" label={e.module} /></TableCell>
                <TableCell><Chip size="small" color="info" label={e.action} /></TableCell>
                <TableCell sx={{ maxWidth: 480, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {JSON.stringify(e.details || {})}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Audit;